import classnames from 'classnames';
import React from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useStoreActions, useStoreState } from '../../store';
import { LanguageMenuScreen } from '../../store/languageMenu';
import { theme } from '../../utils/theme';
import { AudioScreen } from './AudioScreen';
import { FontColorScreen } from './FontColorScreen';
import { FontSizeScreen } from './FontSizeScreen';
import { MobileOptionsScreen } from './MobileOptionsScreen';
import { MobileRootScreen } from './MobileRootScreen';
import { OptionsScreen } from './OptionsScreen';
import { RootScreen } from './RootScreen';
import { SubtitlesScreen } from './SubtitlesScreen';
import { Backdrop, Close, Container, ContentWrapper } from './styles';

const FADE_DURATION_MS = 120;
const UNMOUNT_DELAY_MS = 250;

export const LanguageMenu = () => {
  const controlsVisible = useStoreState((state) => state.player.controlsVisible);
  const languageMenuVisible = useStoreState((state) => state.languageMenu.visible);
  const screen = useStoreState((state) => state.languageMenu.screen);
  const actions = useStoreActions((actions) => actions);
  const isMobile = useMediaQuery(theme.mobileMediaQuery);

  const [mounted, setMounted] = React.useState(false);
  const [transitioned, setTransitioned] = React.useState(false);
  const [contentOpacity, setContentOpacity] = React.useState(1);
  const [displayedScreen, setDisplayedScreen] = React.useState(screen);
  const [containerSize, setContainerSize] = React.useState<{ width?: number; height?: number }>({});

  const contentRef = React.useRef<HTMLDivElement>(null);
  const previousScreenRef = React.useRef(screen);

  const shouldDisplay = controlsVisible && languageMenuVisible;

  React.useEffect(() => {
    setTransitioned(shouldDisplay);

    const timeout = setTimeout(() => {
      if (!shouldDisplay) {
        setDisplayedScreen(LanguageMenuScreen.Root);
        previousScreenRef.current = LanguageMenuScreen.Root;
        setContentOpacity(1);
        setContainerSize({});
      }
      setMounted(shouldDisplay);
    }, shouldDisplay ? 0 : UNMOUNT_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [shouldDisplay]);

  React.useEffect(() => {
    if (screen === previousScreenRef.current) return;

    let cancelled = false;
    let fadeInTimeout: ReturnType<typeof setTimeout>;

    setContentOpacity(0);

    const swapTimeout = setTimeout(() => {
      if (cancelled) return;
      setDisplayedScreen(screen);
      previousScreenRef.current = screen;

      fadeInTimeout = setTimeout(() => {
        if (!cancelled) setContentOpacity(1);
      }, 0);
    }, FADE_DURATION_MS);

    return () => {
      cancelled = true;
      clearTimeout(swapTimeout);
      clearTimeout(fadeInTimeout);
    };
  }, [screen]);

  // Keep the container sized to its content, so width/height animate as the content changes.
  React.useEffect(() => {
    if (!mounted || isMobile || !contentRef.current) return;

    const element = contentRef.current;

    const updateSize = () => {
      const previousWidth = element.style.width;
      element.style.width = 'max-content';
      const { width, height } = element.getBoundingClientRect();
      element.style.width = previousWidth;
      setContainerSize({ width, height });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, [mounted, isMobile]);

  const isMobileOptionsScreen = isMobile && displayedScreen === LanguageMenuScreen.Options;

  function handleClosePress() {
    if (isMobileOptionsScreen) {
      actions.languageMenu.navigateTo(LanguageMenuScreen.Root);
      return;
    }
    actions.languageMenu.setVisible(false);
  }

  function handleBackdropPress() {
    actions.languageMenu.setVisible(false);
  }

  function renderScreen() {
    if (isMobile) {
      switch (displayedScreen) {
        case LanguageMenuScreen.Options:
          return <MobileOptionsScreen />;
        default:
          return <MobileRootScreen />;
      }
    }

    switch (displayedScreen) {
      case LanguageMenuScreen.Subtitles:
        return <SubtitlesScreen />;
      case LanguageMenuScreen.Audio:
        return <AudioScreen />;
      case LanguageMenuScreen.Options:
        return <OptionsScreen />;
      case LanguageMenuScreen.FontSize:
        return <FontSizeScreen />;
      case LanguageMenuScreen.FontColor:
        return <FontColorScreen />;
      case LanguageMenuScreen.Root:
      default:
        return <RootScreen />;
    }
  }

  if (!mounted) return null;

  return (
    <>
      <Backdrop onClick={handleBackdropPress} />
      <Container
        className={classnames(transitioned ? 'visible' : 'hidden')}
        containerWidth={isMobile ? undefined : containerSize.width}
        containerHeight={isMobile ? undefined : containerSize.height}
      >
        <Close
          onClick={handleClosePress}
          isBack={isMobileOptionsScreen}
          aria-label={isMobileOptionsScreen ? 'Back' : 'Close'}
          data-testid="videoPlayer_languageMenuCloseBtn"
        />
        <ContentWrapper
          ref={contentRef}
          contentOpacity={contentOpacity}
          isRootScreen={!isMobile && displayedScreen === LanguageMenuScreen.Root}
        >
          {renderScreen()}
        </ContentWrapper>
      </Container>
    </>
  );
};
